import { CommonModule } from '@angular/common'
import { Component, Input, ViewChild } from '@angular/core'
import { SortDirection } from '@ftr/contracts/api/shared'
import { ComposableDropdownComponent, ComposableDropdownModule, DestroySubscribers } from '@ftr/foundation'
import { takeUntil } from 'rxjs'
import { ComplexFilterService } from '../../complex-filter'
import { RadioListFilter } from '../../complex-filter/RadioListFilter'
import { SortDirectionComponent, SortDirectionText } from '../../sort-direction'

export enum SortByDropdownHeaderSize {
  TranscriptOrders = 'transcript-orders',
  SharedRecordings = 'shared-recordings',
}

/**
 * This component replaces the sort part of the complex filter component on large screens.
 * Doesn't handle sorting or query parameters, communicates all the changes through the service.
 */
@Component({
  selector: 'ftr-complex-list-sort',
  templateUrl: './complex-list-sort.component.html',
  styleUrls: ['./complex-list-sort.component.css'],
  providers: [ComplexFilterService],
  standalone: true,
  imports: [CommonModule, ComposableDropdownModule, SortDirectionComponent],
})
export class ComplexListSortComponent<SortBy extends string = string> extends DestroySubscribers {
  @Input() config: RadioListFilter<SortBy>
  @Input() sortByDropdownHeaderSize: SortByDropdownHeaderSize
  @Input() sortDirectionText?: SortDirectionText | undefined

  @ViewChild(ComposableDropdownComponent) dropdown: ComposableDropdownComponent

  sortBy: SortBy | undefined
  sortDirection: SortDirection

  constructor(private readonly complexFilterService: ComplexFilterService) {
    super()
  }

  ngOnInit(): void {
    this.complexFilterService
      .listenToFilterChange([this.config])
      .pipe(takeUntil(this.finalize))
      .subscribe(params => {
        if (this.hasSortBy) {
          const [sortBy, sortDirection] = params[this.config.filterKey] as string[]
          this.sortBy = sortBy as SortBy
          this.sortDirection = sortDirection as SortDirection
        } else {
          this.sortDirection = params[this.config.filterKey] as SortDirection
        }
      })
  }

  async onSortDirectionChange(sortDirection: SortDirection): Promise<void> {
    this.sortDirection = sortDirection
    return this.updateSort()
  }

  async onSortByChange(sortBy: SortBy): Promise<void> {
    this.dropdown.hideDropDown()
    if (this.sortBy === sortBy) {
      return Promise.resolve()
    }

    this.sortBy = sortBy
    return this.updateSort()
  }

  async updateSort(): Promise<void> {
    await this.complexFilterService.updateFilter({
      [this.config.filterKey]: this.hasSortBy ? [this.sortBy, this.sortDirection] : this.sortDirection,
    })
  }

  /**
   * If sortBy is not supplied, use sortDirection as a single filter
   */
  private get hasSortBy(): boolean {
    return Boolean(this.config.additionalFilters?.length)
  }

  /**
   * We need a fallback in case a random invalid value comes from the query string params
   */
  getSortByLabel(sortBy: SortBy | string): string {
    return this.config.options.find(o => o.value === sortBy)?.key || 'Select'
  }
}
